import { isRecord } from "./util"
import { scrollRestoreConfig } from "./config"

export type ScrollPos = [number, number]
const uniq = "revotale_nextjs_scroll_restoration"
type HistoryKeys =
  | "x"
  | "y"
  | "memo_timestamp"
  | "is_navigating_history"
  | "popstate_timestamp"

export const getKey = (pos: HistoryKeys): string => `${uniq}_${pos}`
export type HistoryState = Record<string, unknown> | null
const defaultX = 0
const defaultY = 0
export const setCurrentScrollHistory = ([x, y]: ScrollPos): void => {
  x = Math.max(x, defaultX)
  y = Math.max(y, defaultY) //Sometime browsers make negative scroll
  const winState = window.history.state as unknown

  const newState: HistoryState = (isRecord(winState) ? winState : null) ?? {}
  window.history.replaceState(
    {
      ...newState,
      [getKey("x")]: x,
      [getKey("y")]: y,
      [getKey("memo_timestamp")]: new Date().getTime(),
    },
    "",
  )
}
const retrieveNum = (name: HistoryKeys, state: HistoryState): number | null => {
  if (state === null) {
    return null
  }
  const key = getKey(name)
  const { [key]: value } = state
  if (value === null) {
    return null
  }
  const num = Number(value)
  return isNaN(num) ? null : num
}
export const getScrollFromState = (state: HistoryState): ScrollPos | null => {
  const x = retrieveNum("x", state)
  const y = retrieveNum("y", state)
  return x !== null && y !== null ? [x, y] : null
}
export const getScrollTimestamp = (state: HistoryState): number | null =>
  retrieveNum("memo_timestamp", state)
export const getPopstateTimestamp = (state: HistoryState): number | null =>
  retrieveNum("popstate_timestamp", state)
export const getIsNavigatingHistory = (state: HistoryState): boolean =>
  state !== null ? Boolean(state[getKey("is_navigating_history")]) : false

// SessionStorage backup functions
const getSessionStorageKey = (pathname: string): string =>
  `${uniq}_session_${pathname}`

export const setScrollToSessionStorage = (
  pathname: string,
  scroll: ScrollPos,
): void => {
  try {
    const key = getSessionStorageKey(pathname)
    sessionStorage.setItem(
      key,
      JSON.stringify({
        x: scroll[0],
        y: scroll[1],
        timestamp: Date.now(),
      }),
    )
  } catch (e) {
    if (scrollRestoreConfig.debug) {
      console.error("Failed to save scroll to sessionStorage:", e)
    }
  }
}

export const getScrollFromSessionStorage = (
  pathname: string,
): ScrollPos | null => {
  try {
    const key = getSessionStorageKey(pathname)
    const data = sessionStorage.getItem(key)
    if (!data) return null

    const parsed = JSON.parse(data)
    if (typeof parsed.x === "number" && typeof parsed.y === "number") {
      return [parsed.x, parsed.y]
    }
    return null
  } catch (e) {
    if (scrollRestoreConfig.debug) {
      console.error("Failed to get scroll from sessionStorage:", e)
    }
    return null
  }
}

export const clearOldSessionStorageEntries = (): void => {
  try {
    const maxAge = 24 * 60 * 60 * 1000 // 24 hours
    const now = Date.now()
    const keysToRemove: string[] = []

    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(uniq)) {
        try {
          const data = sessionStorage.getItem(key)
          if (data) {
            const parsed = JSON.parse(data)
            if (parsed.timestamp && now - parsed.timestamp > maxAge) {
              keysToRemove.push(key)
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          // Invalid data, remove it
          keysToRemove.push(key)
        }
      }
    }

    keysToRemove.forEach((key) => sessionStorage.removeItem(key))
  } catch (e) {
    if (scrollRestoreConfig.debug) {
      console.error("Failed to clear old sessionStorage entries:", e)
    }
  }
}

export const clearNonRootHistoryEntries = (): void => {
  try {
    const keysToRemove: string[] = []
    
    // Find all scroll restoration keys in sessionStorage
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i)
      if (key && key.startsWith(`${uniq}_session_`)) {
        // Extract pathname from key
        const pathname = key.replace(`${uniq}_session_`, "")
        // Remove all entries except root "/"
        if (pathname !== "/") {
          keysToRemove.push(key)
        }
      }
    }
    
    // Remove all non-root entries
    keysToRemove.forEach((key) => {
      sessionStorage.removeItem(key)
      if (scrollRestoreConfig.debug) {
        console.log(`Cleared history entry for: ${key.replace(`${uniq}_session_`, "")}`)
      }
    })
    
    if (keysToRemove.length > 0) {
      if (scrollRestoreConfig.debug) {
        console.log(`Cleared ${keysToRemove.length} non-root history entries from sessionStorage`)
      }
    }
  } catch (e) {
    if (scrollRestoreConfig.debug) {
      console.error("Failed to clear non-root history entries:", e)
    }
  }
}
